// src/pages/RegisterPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { register, login } from '../api/auth';
import { persistOAuthSession } from '../lib/authSession';
import {
  Container,
  Typography,
  TextField,
  Button,
  MenuItem,
  Snackbar,
  Alert,
  Box,
  Avatar,
} from '@mui/material';
import ContextHelpButton from '../components/ContextHelpButton';

export default function RegisterPage() {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    role: '',
    profile_image: null,   // <-- NEW
  });
  const [previewUrl, setPreviewUrl] = useState(null); // <-- NEW (preview)
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // NEW: handle file select + preview
  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((f) => ({ ...f, profile_image: file }));
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Build FormData for multipart, include the image if provided
      const fd = new FormData();
      fd.append('first_name', form.first_name);
      fd.append('last_name', form.last_name);
      fd.append('username', form.username);
      fd.append('email', form.email);
      fd.append('password', form.password);
      fd.append('role', form.role);
      if (form.profile_image) {
        // Backend field name assumed as `profile_image`
        // Change this to `avatar` (or your serializer field) if needed.
        fd.append('profile_image', form.profile_image);
      }

      await register(fd); // register() updated to accept FormData (see below)

      const res = await login(form.username, form.password);
      persistOAuthSession(res.data, form.role);

      setOpen(true);
      if (form.role === 'artist') {
        window.location.href = '/artist-profile/setup';
      } else if (form.role === 'host') {
        window.location.href = '/host-profile/setup';
      } else if (form.role === 'buyer') {
        window.location.href = '/buyer-profile/setup';
      } else {
        setTimeout(() => navigate('/'), 1000);
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setError('Registration failed. Please try again.');
    }
  };

  return (
    <Container>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="h5">Register</Typography>
        <ContextHelpButton section="getting-started" title="Help for account registration" />
      </Box>
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="First Name"
          name="first_name"
          onChange={handleChange}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Last Name"
          name="last_name"
          onChange={handleChange}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Username"
          name="username"
          onChange={handleChange}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Email"
          name="email"
          onChange={handleChange}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          type="password"
          label="Password"
          name="password"
          onChange={handleChange}
          margin="normal"
          required
        />
        <TextField
          select
          fullWidth
          label="Role"
          name="role"
          value={form.role}
          onChange={handleChange}
          margin="normal"
          required
        >
          <MenuItem value="artist">Artist</MenuItem>
          <MenuItem value="host">Host</MenuItem>
          <MenuItem value="buyer">Buyer</MenuItem>
        </TextField>

        {/* NEW: Profile image upload + preview */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={previewUrl || undefined}
            alt="Profile preview"
            sx={{ width: 64, height: 64, border: '1px solid #e5e7eb' }}
          />
          <Button variant="outlined" component="label">
            {form.profile_image ? 'Change Profile Image' : 'Upload Profile Image'}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          {form.profile_image && (
            <Typography variant="caption" color="text.secondary">
              {form.profile_image.name}
            </Typography>
          )}
        </Box>

        <Button type="submit" variant="contained" sx={{ mt: 3 }}>
          Register
        </Button>
      </form>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpen(false)} severity="success" sx={{ width: '100%' }}>
          Registration successful!
        </Alert>
      </Snackbar>
    </Container>
  );
}
